<template>
    <div
        id="sidebar__main"
        tabindex="0"
        role="dialog"
        aria-labelledby="filterSiderbarLabel"
        aria-hidden="true"
        class="sidebar__mobile"
        @focus="selectedDown()"
        @keydown.esc.prevent="closeNav()">
        <div
            id="sideBarHeader"
            class="sidebar__filter sidebar__header"
            :class="{ 'sidebar__toggle' : toggleFilter }">
            <span class="text-upper sidebar__headline">
                {{ trans('FORM_FILTER') }}
            </span>
            <button
                type="button"
                data-gtm="sidebar__close"
                data-pendo="sidebar__close"
                aria-label="Close sidebar"
                aria-controls="close sidebar"
                @click="closeNav()">
                <svg
                    width="20px"
                    height="20px"
                    class="sidebar__icon sidebar__icon--cross"
                    aria-hidden="true">
                    <use href="#iconCrossBold" />
                </svg>
            </button>
        </div>
        <SidebarFilter
            @selected-accuracy="emittedAccuracyValue"
            @selected-contentTypes="emittedContentTypeValue"
            @selected-region="emittedRegionValue"
            @selected-countries="emittedCountriesValue"
            @selected-regionalBoost="emittedRegionalBoostValue"
            @selected-interval="emittedIntervalValue"
            @selected-archive="emittedArchiveValue"
            @selected-sort="emittedSortValue"
            @selected-category="emittedCategoryValue"
            @selected-sub-category="emittedSubCategoryValue"
            @selected-language="emittedLanguageValue"
            @excluded-regions="emittedExcludedRegions"
            @is-filter-open="filterIsOpen" />
        <div class="sidebar__footer">
            <button
                type="button"
                class="sidebar__button sidebar__button--reset"
                aria-controls="sidebarSearchOptions"
                data-gtm="sidebar__Filter--reset"
                data-pendo="sidebar__Filter--reset"
                aria-label="Apply sidebar filters"
                @click="resetFilter()">
                <svg
                    class="sidebar__icon sidebar__icon--reset"
                    aria-hidden="true">
                    <use href="#iconArrowClockwiseBold" />
                </svg>
                {{ trans('RESET_ALL') }}
            </button>
            <button
                :disabled="!changed"
                type="button"
                class="sidebar__button sidebar__button--apply"
                aria-controls="sidebarSearchOptions"
                data-gtm="sidebar__Filter--apply"
                data-pendo="sidebar__Filter--apply"
                aria-label="Reset sidebar filters"
                @click="applyFilter()">
                {{ trans('APPLY_FILTER') }}
            </button>
        </div>
    </div>
</template>
<script>
    import SidebarFilter from './sidebarFilter';
    import Translator from 'tools/trans';
    import { useActions, useGetters } from 'composables/useVuex';
    import { getDiff, isEqual } from './getDiff';
    import { pushToDataLayer } from 'search/dataLayer';
    import { mapMutations } from 'vuex';
    import { defaultLanguageValue } from './store/search.module';

    const AND_VALUE = 'and';
    const ZERO_VALUE = '0';
    const PARAM_OBJECT = {
        INTERVAL: 'interval',
        REGIONAL_BOOST: 'isRegionPref',
        CONTENT_TYPE: 'tabGroup',
        ACCURACY: 'accuracy',
        COUNTRY: 'isoregion',
        LOCATION: 'region[]',
        CATEGORY: 'category[]',
        LANGUAGE: 'language',
        ARCHIVE: 'idRelevance',
    };

    export default {
        name: 'Sidebar',
        components: {
            SidebarFilter,
        },
        data() {
            return {
                toggleFilter: false,
                currentTab: null,
                contentFilters: this.getTabGroup,
                accuracyValue: this.accuracy,
                contentTypeValue: this.getTabGroup,
                intervalValue: this.interval,
                countriesValue: this.location,
                idRelevanceValue: this.idRelevance,
                categoryValue: this.category,
                subCategoryValue: this.subCategory,
                regionValue: this.isoregion,
                isRegionPrefValue: this.focus,
                sortValue: this.sort,
                languageValue: this.language,
                excludedRegions: [],
                tabs: {
                    contentTypes: this.trans('CONTENT_TYPES'),
                    accuracy: this.trans('ACCURACY'),
                    location: this.trans('GLOBAL_LOCATION'),
                    industry: this.trans('FORM_INDUSTRY'),
                    publicationDate: this.trans('FORM_SEARCH_INTERVAL'),
                    archive: this.trans('FORM_SEARCH_ARCHIVE_LABEL'),
                },
                changed: false,
                initialObj: {
                    sort: this.sort,
                    contentTypes: this.getTabGroup,
                    isoregion: this.isoregion,
                    countries: !Array.isArray(this.location) ?
                        this.location.split(',').map(function(x) {
                            return parseInt(x);
                        })
                        :
                        this.location
                    ,
                    isRegionPref: this.focus,
                    accuracy: this.accuracy,
                    interval: this.interval,
                    idRelevance: this.idRelevance,
                    category: this.category,
                    subCategory: this.subCategory,
                    language: this.language,
                },
            };
        },
        computed: {
            temporaryObj() {
                return {
                    sort: this.sortValue,
                    contentTypes: this.contentFilters,
                    isoregion: this.regionValue,
                    countries: this.countriesValue,
                    accuracy: this.accuracyValue,
                    interval: this.intervalValue,
                    idRelevance: this.idRelevanceValue,
                    category: this.categoryValue,
                    subCategory: this.subCategoryValue,
                    isRegionPref: this.isRegionPrefValue,
                    language: this.languageValue,
                };
            },
        },
        watch: {
            temporaryObj: {
                handler(value) {
                    this.changed = this.checkIfChanged(this.initialObj, value);
                },
                deep: true,
            },
        },
        methods: {
            ...mapMutations('search', ['setSelectedFilterNumber']),
            trans: Translator.trans,
            updateContentTypes(value) {
                this.contentFilters = value;
            },
            emittedAccuracyValue(filterValue) {
                this.accuracyValue = filterValue;
            },
            emittedContentTypeValue(filterValue) {
                this.contentFilters = filterValue;
            },
            emittedCountriesValue(filterValue) {
                this.countriesValue = filterValue;
            },
            emittedIntervalValue(filterValue) {
                this.intervalValue = filterValue;
            },
            emittedArchiveValue(filterValue) {
                this.idRelevanceValue = filterValue;
            },
            emittedCategoryValue(filterValue) {
                this.categoryValue = filterValue;
            },
            emittedSubCategoryValue(filterValue) {
                this.subCategoryValue = filterValue;
            },
            emittedRegionValue(filterValue) {
                this.regionValue = filterValue;
            },
            emittedSortValue(filterValue) {
                this.sortValue = filterValue;
            },
            emittedRegionalBoostValue(filterValue) {
                this.isRegionPrefValue = filterValue;
            },
            emittedLanguageValue(filterValue) {
                this.languageValue = filterValue;
            },
            emittedExcludedRegions(data) {
                this.excludedRegions = data;
            },
            filterIsOpen(toggle){
                this.toggleFilter = toggle;
            },
            async resetFilter() {
                await this.setSelectedFilterNumber(ZERO_VALUE);
                await this.resetFilters();
            },
            checkIfChanged(obj1, obj2) {
                for (const key in obj1) {
                    if (Array.isArray(obj1[key])) {
                        if (isEqual(obj1[key], obj2[key])) {
                            continue;
                        } else {
                            return true;
                        }
                    } if (obj1[key] === obj2[key]) {
                        continue;
                    } else {
                        return true;
                    }
                }

                return false;
            },
            async applyFilter() {
                const diffObj = getDiff(this.initialObj, this.temporaryObj);
                if (this.excludedRegions.includes(parseInt(this.temporaryObj.isoregion))) {
                    if (diffObj['countries']) {
                        diffObj['countries'] = [];
                    }
                }
                await this.selectFilters(diffObj);

                const queryString = window.location.search;
                const uparams = new URLSearchParams(queryString);

                let numberOfSelectedFilters = 0;
                if (uparams) {
                    if (uparams.get(PARAM_OBJECT.INTERVAL) && uparams.get(PARAM_OBJECT.INTERVAL) !== ZERO_VALUE) {
                        numberOfSelectedFilters++;
                    }
                    if (uparams.get(PARAM_OBJECT.CONTENT_TYPE)) {
                        numberOfSelectedFilters = numberOfSelectedFilters + uparams.get(PARAM_OBJECT.CONTENT_TYPE).split(',').length;
                    }
                    if (uparams.get(PARAM_OBJECT.ACCURACY) && uparams.get(PARAM_OBJECT.ACCURACY) !== AND_VALUE) {
                        numberOfSelectedFilters++;
                    }
                    if (uparams.get(PARAM_OBJECT.COUNTRY)) {
                        if (uparams.get(PARAM_OBJECT.COUNTRY) !== ZERO_VALUE) {
                            numberOfSelectedFilters++;
                        } else if (uparams.get(PARAM_OBJECT.LOCATION) && uparams.get(PARAM_OBJECT.LOCATION) !== '') {
                            numberOfSelectedFilters++;
                        }
                    } else if (uparams.get(PARAM_OBJECT.LOCATION) && uparams.get(PARAM_OBJECT.LOCATION) !== '') {
                        numberOfSelectedFilters++;
                    }
                    if (uparams.get(PARAM_OBJECT.ARCHIVE) && uparams.get(PARAM_OBJECT.ARCHIVE) !== ZERO_VALUE) {
                        numberOfSelectedFilters++;
                    }
                    if (uparams.get(PARAM_OBJECT.CATEGORY) && uparams.get(PARAM_OBJECT.CATEGORY) !== ZERO_VALUE) {
                        numberOfSelectedFilters++;
                    }
                    if (uparams.get(PARAM_OBJECT.LANGUAGE) && uparams.get(PARAM_OBJECT.LANGUAGE) !== defaultLanguageValue[window.stGlobals.locale]) {
                        numberOfSelectedFilters++;
                    }
                }
                this.setSelectedFilterNumber(numberOfSelectedFilters);
                await this.calculateWhichBannerToShow();
                this.closeNav();
            },
            selectedDown() {
                document.getElementById('sidebar__contentId').tabIndex = '0';
                document.getElementById('sidebar__contentId').focus();
            },
        },
        setup(_props, context) {
            const getters = {
                ...useGetters('search', [
                    'query',
                    'resultCount',
                    'getMetric',
                    'getOriginalMetric',
                    'resultArray',
                    'masterTags',
                    'promotedArray',
                    'getTabGroup',
                    'entity',
                    'entityName',
                    'getTitle',
                    'getContent',
                    'currentPage',
                    'totalFound',
                    'totalPages',
                    'loading',
                    'activeTab',
                    'topic',
                    'topics',
                    'countries',
                    'path',
                    'sort',
                    'focus',
                    'location',
                    'archive',
                    'tags',
                    'getShowSidebar',
                    'intervalData',
                    'interval',
                    'accuracy',
                    'idRelevance',
                    'category',
                    'subCategory',
                    'isoregion',
                    'language',
                ]),
            };
            const actions = {
                ...useActions('search', [
                    'resetFilters',
                    'resetPageNumber',
                    'setFilters',
                    'calculateWhichBannerToShow',

                ]),
            };
            async function selectFilters(diffFilters) {
                await actions.resetPageNumber();
                await actions.setFilters(diffFilters);
                // push to data layer
                setTimeout(() => {
                    pushToDataLayer();
                }, 5000);
            }
            async function closeNav() {
                context.emit('closeSidebar', false);
            }
            async function resetFilters() {
                await actions.resetPageNumber();
                await closeNav();
                actions.resetFilters();
            }
            async function calculateWhichBannerToShow() {
                await actions.calculateWhichBannerToShow();
            }

            return {
                selectFilters,
                resetFilters,
                closeNav,
                calculateWhichBannerToShow,
                ...getters,
            };
        },
    };
</script>
